import {Grid, Typography, useMediaQuery} from '@mui/material';
import {Fragment, useMemo} from 'react';
import {Divider} from '@mui/material';
import {customScrollbarStyles} from '../../pages/Dashboard/dashboard';

export const handleFormattingByMetricKey = (value, metricKey) => {
  if (metricKey === undefined) {
    metricKey = 'overall_wellness';
  }
  if (metricKey.match(/^(heart_rate_change)|(stresss_index)$/)) return parseFloat(Number(value.toFixed(1)));
  if (metricKey.match(/^(cardiac_workload)|(vascular_capacity)$/)) return parseFloat(Number(value.toFixed(2)));
  return parseInt(Math.round(value).toFixed(0));
};

/**
 * VitalsList component
 * Renders vital list data. Particularly the metric unit itself and when this was captured. More details of data object attributes in function definition
 * @param {Object} props - Component props
 * @param {string} props.unit - Unit used by this metric's data
 * @param {Array.<{
 *   x: Date // Date Object
 *   y: Number // metric number value
 * }>} props.data - Array of objects containing the specific data that's going to be shown
 * @param {string} props.metricKey - Valid identificator for the metric(the ones returned from the BE)
 *
 * @returns {React.Component} The rendered SubNavbar component.
 */
export const VitalsList = ({data, unit, metricKey}) => {
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const sortedData = useMemo(() => {
    const newData = [...data].sort((a, b) => b.x - a.x);
    return newData;
  }, [data]);

  return (
    <Grid
      container
      sx={{
        overflow: 'auto',
        justifyContent: 'flex-start',
        p: 2,
        px: isSmallScreen ? 3 : 8,
        ...customScrollbarStyles,
      }}
    >
      {sortedData.map((object, idx) => {
        const dateObject = object.x;
        const formattedDate = dateObject
          .toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          })
          .toUpperCase();
        const weekday = dateObject
          .toLocaleDateString('en-US', {
            weekday: 'long',
          })
          .toUpperCase();
        const time = dateObject.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: 'numeric',
          hour12: true,
        });

        return (
          <Fragment key={idx}>
            <Grid
              item
              container
              columns={16}
              columnGap={{
                xs: 0,
                sm: 4,
                lg: 2,
              }}
              xs={16}
              justifyContent={'space-between'}
            >
              <Grid item xs={6} sm={2} lg={3} sx={{display: 'flex', alignItems: 'center'}}>
                <Typography textAlign="start" fontSize={isSmallScreen ? 14 : 16} lineHeight={1.2}>
                  {formattedDate}
                  <br />
                  <span style={{fontSize: 14}}>{weekday}</span>
                </Typography>
              </Grid>
              <Grid item xs={5} sm={4} md={5} sx={{display: 'flex', alignItems: 'center'}}>
                <Typography fontSize={isSmallScreen ? 14 : 16}>{time}</Typography>
              </Grid>
              <Grid
                item
                xs={5}
                sm={4}
                md={5}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'end',
                }}
              >
                <Typography>
                  <span
                    style={{
                      fontSize: isSmallScreen ? 18 : 20,
                      fontWeight: 600,
                      color: '',
                    }}
                  >
                    {handleFormattingByMetricKey(object.y, metricKey)}
                  </span>{' '}
                  <span style={{fontSize: isSmallScreen ? 14 : 16}}>{unit}</span>
                </Typography>
              </Grid>
            </Grid>
            {idx !== data.length - 1 ? (
              <Grid item xs={12} sx={{height: 'fit-content'}}>
                <Divider sx={{my: 1.5}} />
              </Grid>
            ) : (
              ''
            )}
          </Fragment>
        );
      })}
    </Grid>
  );
};
